import { Subject } from '~/rage/utils';
import {
	FishRarity,
	getRandomArrayElement,
	getRandomInt,
	HobbyFishingMinigameConfig,
} from '~/shared';
import {
	DEFAULT_FISHING_DIFFICULTY,
	getFishingDifficultyByRarity,
} from './fishingDifficulty';
import {
	IBait,
	IGameFishingService,
	IHobbyFishingDifficulty,
	IHobbyFishingHookedFish,
} from './gameFishingServiceTypes';

const STUB_AVAILABLE_BAITS: IBait[] = [
	{ itemId: 'dough', count: 5 },
	{ itemId: 'bread', count: 3 },
	{ itemId: 'worm', count: 12 },
	{ itemId: 'corn', count: 8 },
	{ itemId: 'maggots', count: 20 },
	{ itemId: 'flies', count: 7 },
	{ itemId: 'shrimps', count: 4 },
	{ itemId: 'boilies', count: 2 },
];

const STUB_FISHES: Record<FishRarity, IHobbyFishingHookedFish> = {
	[FishRarity.COMMON]: {
		fishId: 'catfish',
		rarity: FishRarity.COMMON,
		name: 'Сом',
		weight: 3.54,
		expCatch: 5,
		expRelease: 1,
		description: 'Обычная рыба, ловится почти на любую прикормку',
	},
	[FishRarity.RARE]: {
		fishId: 'pike',
		rarity: FishRarity.RARE,
		name: 'Щука',
		weight: 6.12,
		expCatch: 15,
		expRelease: 4,
		description: 'Редкая рыба, любит холодные заливы',
	},
	[FishRarity.TROPHY]: {
		fishId: 'sturgeon',
		rarity: FishRarity.TROPHY,
		name: 'Осётр',
		weight: 18.7,
		expCatch: 40,
		expRelease: 12,
		description: 'Трофейная рыба, сорвётся при первой ошибке',
	},
	[FishRarity.FORBIDDEN]: {
		fishId: 'shark',
		rarity: FishRarity.FORBIDDEN,
		name: 'Акула',
		weight: 64.3,
		expCatch: 90,
		expRelease: 30,
		description: 'Запрещённая рыба, за такую можно и срок получить',
	},
};

export class GameFishingServiceStub implements IGameFishingService {
	private avaliableBaits = new Subject<IBait[]>(STUB_AVAILABLE_BAITS);
	private backpackAvaliable = new Subject<boolean>(false);

	private hookedFish = new Subject<IHobbyFishingHookedFish | null>(null);
	private difficulty = new Subject<IHobbyFishingDifficulty>(
		DEFAULT_FISHING_DIFFICULTY,
	);

	private minigameConfig = new Subject<HobbyFishingMinigameConfig>({
		fishEscapeChance: 0.001,
		waitGreenSecMin: 5,
		waitGreenSecMax: 10,
		waitOrangeSecMin: 10,
		waitOrangeSecMax: 15,
		waitOtherSecMin: 15,
		waitOtherSecMax: 30,
	});

	public async reportWin(isBackpackEnabled: boolean): Promise<void> {
		console.log('GameFishingService: reportWin', { isBackpackEnabled });
	}

	public getHookedFish(): Subject<IHobbyFishingHookedFish | null> {
		return this.hookedFish;
	}

	public getDifficulty(): Subject<IHobbyFishingDifficulty> {
		return this.difficulty;
	}

	public async reportLoss(): Promise<void> {
		console.log('GameFishingService: reportLoss');
		this.resetHookedFish();
	}

	public start(_baitItemId?: string): Promise<void> {
		console.log('GameFishingService: start');

		const rarity = getRandomArrayElement(Object.values(FishRarity));
		const fish = STUB_FISHES[rarity];

		this.hookedFish.next({
			...fish,
			weight: Math.round(fish.weight * getRandomInt(70, 130)) / 100,
		});
		this.difficulty.next(getFishingDifficultyByRarity(rarity));

		return Promise.resolve();
	}

	public stop(): Promise<void> {
		console.log('GameFishingService: stop');
		this.resetHookedFish();
		return Promise.resolve();
	}

	public releaseFish(fishItemId: string): Promise<void> {
		console.log('GameFishingService: releaseFish', { fishItemId });
		return Promise.resolve();
	}

	public getAvaliableBaits(): Subject<IBait[]> {
		return this.avaliableBaits;
	}

	public getBackpackAvaliable(): Subject<boolean> {
		return this.backpackAvaliable;
	}

	public getMinigameConfig(): Subject<HobbyFishingMinigameConfig> {
		return this.minigameConfig;
	}

	public async exit(): Promise<void> {
		console.log('GameFishingService: exit');
		this.resetHookedFish();
	}

	private resetHookedFish(): void {
		this.hookedFish.next(null);
		this.difficulty.next(DEFAULT_FISHING_DIFFICULTY);
	}
}
